import { useState, useEffect } from "react";

/* ─── Settings stored in localStorage ───────────────────────── */
const LS_KEY = "pixory_appt_settings";

interface ApptSettings {
  defaultDuration: number;        // minutes
  bufferTime: number;             // minutes between appts
  workingDays: number[];          // 0=Sun .. 6=Sat
  defaultShowInPortal: boolean;
  defaultStartHour: number;       // 9 = 9am
  defaultEndHour: number;         // 18 = 6pm
}

const DEFAULT_SETTINGS: ApptSettings = {
  defaultDuration: 60,
  bufferTime: 15,
  workingDays: [1, 2, 3, 4, 5],
  defaultShowInPortal: true,
  defaultStartHour: 9,
  defaultEndHour: 18,
};

const loadSettings = (): ApptSettings => {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return { ...DEFAULT_SETTINGS, ...JSON.parse(raw) };
  } catch { /* ignore */ }
  return { ...DEFAULT_SETTINGS };
};

const DAY_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const DURATION_OPTS = [
  { value: 15,  label: "15 minutes" },
  { value: 30,  label: "30 minutes" },
  { value: 45,  label: "45 minutes" },
  { value: 60,  label: "1 hour" },
  { value: 90,  label: "1.5 hours" },
  { value: 120, label: "2 hours" },
];

const BUFFER_OPTS = [
  { value: 0,  label: "No buffer" },
  { value: 5,  label: "5 minutes" },
  { value: 10, label: "10 minutes" },
  { value: 15, label: "15 minutes" },
  { value: 30, label: "30 minutes" },
];

const HOUR_OPTS = Array.from({ length: 24 }, (_, i) => {
  const h = i % 12 || 12;
  const ampm = i < 12 ? "am" : "pm";
  return { value: i, label: `${h}:00 ${ampm}` };
});

/* ─── Component ──────────────────────────────────────────────── */
export default function AppointmentsSettingsTab() {
  const [settings, setSettings] = useState<ApptSettings>(loadSettings());
  const [saved, setSaved]       = useState(false);

  useEffect(() => { setSaved(false); }, [settings]);

  const set = <K extends keyof ApptSettings>(key: K, value: ApptSettings[K]) =>
    setSettings((prev) => ({ ...prev, [key]: value }));

  const toggleDay = (day: number) => {
    const days = settings.workingDays.includes(day)
      ? settings.workingDays.filter((d) => d !== day)
      : [...settings.workingDays, day].sort();
    set("workingDays", days);
  };

  const handleSave = () => {
    localStorage.setItem(LS_KEY, JSON.stringify(settings));
    setSaved(true);
    setTimeout(() => setSaved(false), 2500);
  };

  return (
    <div className="max-w-2xl space-y-6">

      {/* Working hours */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-[#0f172a] font-semibold text-sm mb-1">Working Hours</h3>
        <p className="text-slate-400 text-xs mb-4">Default business hours shown in the calendar view.</p>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Start time</label>
            <select
              value={settings.defaultStartHour}
              onChange={(e) => set("defaultStartHour", Number(e.target.value))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none bg-white cursor-pointer"
            >
              {HOUR_OPTS.filter((o) => o.value < 22).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">End time</label>
            <select
              value={settings.defaultEndHour}
              onChange={(e) => set("defaultEndHour", Number(e.target.value))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none bg-white cursor-pointer"
            >
              {HOUR_OPTS.filter((o) => o.value > 1).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-slate-500 mb-2">Working days</label>
          <div className="flex items-center gap-2 flex-wrap">
            {DAY_LABELS.map((label, i) => (
              <button
                key={i}
                onClick={() => toggleDay(i)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all cursor-pointer whitespace-nowrap ${
                  settings.workingDays.includes(i)
                    ? "bg-[#C8A96E] border-[#C8A96E] text-white"
                    : "bg-white border-slate-200 text-slate-400 hover:border-slate-300"
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Appointment defaults */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-[#0f172a] font-semibold text-sm mb-1">Appointment Defaults</h3>
        <p className="text-slate-400 text-xs mb-4">These values are used when creating a new appointment.</p>

        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Default duration</label>
            <select
              value={settings.defaultDuration}
              onChange={(e) => set("defaultDuration", Number(e.target.value))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none bg-white cursor-pointer"
            >
              {DURATION_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1.5">Buffer between appointments</label>
            <select
              value={settings.bufferTime}
              onChange={(e) => set("bufferTime", Number(e.target.value))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm text-[#0f172a] outline-none bg-white cursor-pointer"
            >
              {BUFFER_OPTS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>

        <label className="flex items-center gap-3 cursor-pointer select-none">
          <div
            onClick={() => set("defaultShowInPortal", !settings.defaultShowInPortal)}
            className={`w-10 h-5 rounded-full transition-colors relative cursor-pointer ${settings.defaultShowInPortal ? "bg-[#C8A96E]" : "bg-slate-200"}`}
          >
            <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${settings.defaultShowInPortal ? "translate-x-5" : "translate-x-0.5"}`} />
          </div>
          <span className="text-sm text-slate-600">Show new appointments in client portal by default</span>
        </label>
      </div>

      {/* Google Calendar coming soon */}
      <div className="bg-slate-50 rounded-xl border border-slate-200 p-5">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-slate-200 shrink-0">
            <i className="ri-google-line text-slate-400 text-base"></i>
          </div>
          <div>
            <p className="text-[#0f172a] font-semibold text-sm">Google Calendar Sync</p>
            <p className="text-slate-400 text-xs mt-0.5 leading-relaxed">
              Two-way sync with Google Calendar is coming soon. Your appointments will automatically appear in your Google Calendar and vice versa.
            </p>
            <span className="inline-flex items-center gap-1 mt-2 px-2 py-1 bg-amber-50 border border-amber-200 rounded-lg text-[10px] text-amber-600 font-medium">
              <i className="ri-time-line text-[10px]"></i>Coming soon
            </span>
          </div>
        </div>
      </div>

      {/* Save button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleSave}
          className="flex items-center gap-2 bg-[#C8A96E] hover:bg-[#b8975e] text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors cursor-pointer whitespace-nowrap"
        >
          <i className="ri-save-line text-sm"></i>
          Save Settings
        </button>
        {saved && (
          <span className="flex items-center gap-1.5 text-emerald-600 text-sm font-medium">
            <i className="ri-checkbox-circle-line"></i>
            Saved!
          </span>
        )}
      </div>
    </div>
  );
}